import React from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './NativeAmericanTrainCategories.css';

const NativeAmericanTrainCategories = () => {
    const navigate = useNavigate();

    // Native American trainer category options with regional themes
    const nativeAmericanTrainerCategories = [
        {
            id: 'native-american-one-trainers',
            title: 'Great Plains Warriors',
            description: 'Train with legendary Lakota and Cheyenne war leaders',
            image: '/assets/images/native-american-one-trainers.jpg',
            route: '/native-american-one-trainers',
            color: '#C0C0C0', // Metallic silver
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Vision Quest Power', 'Lightning Warfare', 'Sacred Resistance']
        },
        {
            id: 'native-american-two-trainers',
            title: 'Desert & Mountain Warriors',
            description: 'Train with legendary Apache and Comanche desert fighters',
            image: '/assets/images/native-american-two-trainers.jpg',
            route: '/native-american-two-trainers',
            color: '#4169E1', // Royal blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Desert Lightning', 'Sacred Buffalo Power', 'Mountain Fortress']
        },
        {
            id: 'native-american-three-trainers',
            title: 'Eastern Woodland Leaders',
            description: 'Train with legendary forest warriors and diplomatic chiefs',
            image: '/assets/images/native-american-three-trainers.jpg',
            route: '/native-american-three-trainers',
            color: '#228B22', // Forest green
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Pan-Indian Unity', 'Siege Mastery', 'Forest Warfare']
        },
        {
            id: 'native-american-four-trainers',
            title: 'Strategic Masters & Coastal Chiefs',
            description: 'Train with legendary tacticians and mountain warriors',
            image: '/assets/images/native-american-four-trainers.jpg',
            route: '/native-american-four-trainers',
            color: '#4169E1', // Strategic blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Strategic Brilliance', 'Mountain Warfare', 'Swamp Mastery']
        },
        {
            id: 'native-american-five-trainers',
            title: 'Legendary Spirits & Final Warriors',
            description: 'Train with the ultimate legends of Native American history',
            image: '/assets/images/native-american-five-trainers.jpg',
            route: '/native-american-five-trainers',
            color: '#8A2BE2', // Purple mystical
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Intellectual Warfare', 'Female Warrior Power', 'Indomitable Spirit']
        }
    ];

    // Handle trainer category selection
    const handleCategorySelect = (category) => {
        console.log('Native American trainer category clicked:', category);

        // 🎵 PLAY CATEGORY-SPECIFIC SOUND
        if (category.sound) {
            soundManager.playSound(category.sound, 0.7);
        } else {
            soundManager.playButtonSound();
        }

        // Navigate to the specific Native American trainer category page
        navigate(category.route);
    };

    // Handle back to home
    const handleBackToHome = () => {
        soundManager.playButtonSound();
        navigate('/');
    };

    return (
        <div className="native-train-categories-container">
            <div className="native-animated-background"></div>

            <header className="native-train-categories-header">
                <button
                    className="back-to-home-button native-back-button"
                    onClick={handleBackToHome}
                >
                    ← Back to Home
                </button>

                <div className="header-content">
                    <h1>Native American Training Masters</h1>
                    <p>Choose your path to train with legendary warriors, chiefs, and spiritual leaders</p>
                    <div className="native-symbol">🪶</div>
                </div>
            </header>

            <div className="native-trainer-categories-grid">
                {nativeAmericanTrainerCategories.map((category) => (
                    <div
                        key={category.id}
                        className="native-trainer-category-card-link"
                        onClick={() => handleCategorySelect(category)}
                    >
                        <div
                            className="native-trainer-category-card"
                            data-category-id={category.id}
                            style={{
                                '--native-theme-color': category.color
                            }}
                        >
                            <div className="native-trainer-category-image">
                                <div
                                    className="native-image-placeholder"
                                    style={{
                                        backgroundColor: category.color,
                                        backgroundImage: `url(${category.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="native-trainer-count-badge">
                                        {category.trainerCount} Warriors
                                    </div>
                                    <div className="native-overlay"></div>
                                </div>
                            </div>
                            <div className="native-trainer-category-info">
                                <h2 style={{ color: category.color }}>{category.title}</h2>
                                <p>{category.description}</p>
                                <div className="native-category-features">
                                    {category.features.map((feature, index) => (
                                        <span key={index} className="native-feature-badge">
                                            🪶 {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="native-theme-indicator" style={{ backgroundColor: category.color }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="native-train-categories-footer">
                <div className="native-train-info">
                    <h3>🏔️ Native American Training Philosophy</h3>
                    <div className="native-info-grid">
                        <div className="native-info-item">
                            <span className="native-info-icon">🪶</span>
                            <div>
                                <strong>Legendary Warriors</strong>
                                <p>Train with 30 legendary figures from Native American history and tradition</p>
                            </div>
                        </div>
                        <div className="native-info-item">
                            <span className="native-info-icon">🦅</span>
                            <div>
                                <strong>Sacred Connection</strong>
                                <p>Experience training that connects physical strength with spiritual wisdom</p>
                            </div>
                        </div>
                        <div className="native-info-item">
                            <span className="native-info-icon">🌿</span>
                            <div>
                                <strong>Ancestral Heritage</strong>
                                <p>Connect with centuries of Native American warrior and cultural traditions</p>
                            </div>
                        </div>
                        <div className="native-info-item">
                            <span className="native-info-icon">🏕️</span>
                            <div>
                                <strong>Diverse Nations</strong>
                                <p>From Plains warriors to desert fighters, explore every aspect of Native training</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="native-flow-info">
                    <div className="native-divider"></div>
                    <p>💡 <strong>Path:</strong> Home → Native Train → Warriors → Trainer → Intensity → Workout</p>
                    <p className="native-wisdom">"Honor the sacred. Honor the Earth, our Mother. Honor all with whom we share the Earth: Four-leggeds, two-leggeds, winged ones, swimmers, crawlers, plant and rock people." - Native American Prayer</p>
                </div>
            </footer>
        </div>
    );
};

export default NativeAmericanTrainCategories;